if (!customElements.get('vl-pdp-feature-media')) {
  class VLPDPFeatureMedia extends HTMLElement {
    constructor() {
      super();

      this.frame = null;
      this.observer = null;
      this.resizeObserver = null;
      this.isNearViewport = false;
      this.lastOffset = null;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized || this.dataset.scrollMediaEffect !== 'true') return;

      this.viewport = this.querySelector('[data-vl-pdp-feature-media-motion-viewport]');
      this.motionContent = this.querySelector('[data-vl-pdp-feature-media-motion-content]');
      if (!this.viewport || !this.motionContent) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 767px)');

      window.addEventListener('scroll', this.handleScroll, { passive: true, signal });
      window.addEventListener('resize', this.handleResize, { passive: true, signal });
      this.motionPreference.addEventListener('change', this.handleMotionPreferenceChange, { signal });
      this.mobileQuery.addEventListener('change', this.handleResize, { signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.viewport);
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.handleIntersection, {
          rootMargin: '50% 0px',
          threshold: 0,
        });
        this.observer.observe(this.viewport);
      } else {
        this.isNearViewport = true;
      }

      this.scheduleUpdate(true);
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.abortController?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.frame !== null) cancelAnimationFrame(this.frame);

      this.style.removeProperty('--vl-pdp-feature-media-motion-y');
      this.abortController = null;
      this.observer = null;
      this.resizeObserver = null;
      this.frame = null;
      this.isNearViewport = false;
      this.lastOffset = null;
      this.isInitialized = false;
    }

    handleScroll() {
      if (this.isNearViewport) this.scheduleUpdate();
    }

    handleResize() {
      this.lastOffset = null;
      this.scheduleUpdate(true);
    }

    handleIntersection(entries) {
      const entry = entries[entries.length - 1];
      if (!entry) return;

      this.isNearViewport = entry.isIntersecting;
      if (this.isNearViewport) this.scheduleUpdate(true);
    }

    handleMotionPreferenceChange() {
      this.lastOffset = null;
      this.scheduleUpdate(true);
    }

    scheduleUpdate(force = false) {
      if (this.frame !== null && !force) return;
      if (this.frame !== null) cancelAnimationFrame(this.frame);

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateMotion();
      });
    }

    updateMotion() {
      if (this.motionPreference.matches) {
        this.setOffset(0);
        return;
      }

      const rect = this.viewport.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.height <= 0 || viewportHeight <= 0) return;

      const totalDistance = viewportHeight + rect.height;
      const progress = this.clamp((viewportHeight - rect.top) / totalDistance, 0, 1);
      const distance = this.getMotionDistance();
      const maxOffset = rect.height * (distance / 100);
      const offset = (1 - progress * 2) * maxOffset;

      this.setOffset(offset);
    }

    getMotionDistance() {
      const styles = getComputedStyle(this);
      const property = this.mobileQuery.matches
        ? '--vl-pdp-feature-media-motion-distance-mobile'
        : '--vl-pdp-feature-media-motion-distance';

      return Number.parseFloat(styles.getPropertyValue(property)) || 0;
    }

    setOffset(offset) {
      const roundedOffset = Math.round(offset * 100) / 100;
      if (roundedOffset === this.lastOffset) return;

      this.lastOffset = roundedOffset;
      this.style.setProperty('--vl-pdp-feature-media-motion-y', `${roundedOffset}px`);
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }
  }

  customElements.define('vl-pdp-feature-media', VLPDPFeatureMedia);
}
